import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../auth";
import {
    MeetingBackgroundsAPI,
    uploadBackgroundImage,
} from "../infrastructure/api/MeetingBackgrounds.api";
import { backgroundContentType } from "../domain/meetingBackground";

const KEY = "meeting-backgrounds";
const CENTER_KEY = "meeting-backgrounds-center";

/**
 * The therapist's own pictures, newest first.
 *
 * Asked for only with an account behind it. The same room is opened by a
 * patient joining as a guest, who has no shelf and no token to ask for one —
 * and whose picker should show the blur and stop there, not a failed request.
 */
export const useMeetingBackgrounds = () => {
    const { isAuthenticated } = useAuth();
    return useQuery({
        queryKey: [KEY],
        queryFn: async () => {
            const { data } = await MeetingBackgroundsAPI.list();
            return data;
        },
        enabled: isAuthenticated,
    });
};

/** Their centre's shelf. Empty for a therapist in none. */
export const useCenterMeetingBackgrounds = () => {
    const { isAuthenticated } = useAuth();
    return useQuery({
        queryKey: [CENTER_KEY],
        queryFn: async () => {
            const { data } = await MeetingBackgroundsAPI.listCenter();
            return data;
        },
        enabled: isAuthenticated,
    });
};

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: [KEY] });
    queryClient.invalidateQueries({ queryKey: [CENTER_KEY] });
};

/**
 * Adding a picture: ask for a slot, PUT the bytes, then register it.
 *
 * The format is resolved from the file rather than trusted from it: a browser
 * that reports nothing for a `.jpg` dragged from a file manager would otherwise
 * be refused a slot for a picture we can perfectly well composite.
 */
export const useAddMeetingBackground = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            file,
            name,
            forCenter = false,
        }: {
            file: File;
            name: string;
            forCenter?: boolean;
        }) => {
            const contentType = backgroundContentType(file);
            const { data: slot } = await MeetingBackgroundsAPI.uploadUrl(contentType);
            await uploadBackgroundImage(slot.upload_url, file, contentType);
            const { data } = await MeetingBackgroundsAPI.create({
                name,
                storage_path: slot.storage_path,
                content_type: contentType,
                for_center: forCenter || undefined,
            });
            return data;
        },
        onSuccess: () => invalidate(queryClient),
    });
};

export const useDeleteMeetingBackground = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => MeetingBackgroundsAPI.remove(id),
        onSuccess: () => invalidate(queryClient),
    });
};

/**
 * A URL for drawing one, since the file is not public.
 *
 * The bytes come through our API and become a blob URL, for the same reason a
 * track's do: the bucket answers no browser directly, and the processor draws
 * the picture into the same canvas as the camera frame — which a cross-origin
 * image would taint.
 *
 * Every caller owns what it gets back and must revoke it.
 */
export const meetingBackgroundImageUrl = async (id: string): Promise<string> => {
    const { data } = await MeetingBackgroundsAPI.image(id);
    return URL.createObjectURL(data);
};
